<template>
	<component :is="'div'">
		<!-- Alert: No item found -->
		<b-alert
			variant="danger"
			:show="userData === undefined"
		>
			<h4 class="alert-heading">Error al obtener los datos del usuario</h4>
			<div class="alert-body">
				No se encontró ningún usuario con este id. Verificar en la
				<b-link
					class="alert-link"
					:to="{ name: 'users-list' }"
				>Lista de usuarios</b-link>para buscar otro usuario.
			</div>
		</b-alert>

		<validation-observer
			v-if="userData"
			ref="refForm"
		>
			<b-form
				@submit.prevent="onSubmit"
				@reset.prevent="resetForm"
			>
				<b-row>
					<b-col md="12">
						<b-card
							footer-tag="footer"
							class="mb-0"
						>
							<b-row>
								<!-- NAMES -->
								<b-col
									md="4"
									cols="12"
									class="mb-2"
								>
									<label for="names">
										<strong>
											NOMBRE(S)
											<span class="text-danger">*</span>
										</strong>
									</label>
									<validation-provider
										#default="{ errors }"
										name="Nombre(s)"
									>
										<b-form-input
											id="names"
											v-model="formData.names"
											placeholder="Ingrese nombre(s) ..."
											:state="errors.length > 0 ? false : null"
											trim
										/>

										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-col>

								<!-- SURNAMES -->
								<b-col
									md="4"
									cols="12"
									class="mb-2"
								>
									<label for="surnames">
										<strong>APELLIDOS(S)</strong>
									</label>
									<validation-provider
										#default="{ errors }"
										name="Apellido(s)"
									>
										<b-form-input
											id="surnames"
											v-model="formData.surnames"
											placeholder="Ingrese apellido(s) ..."
											:state="errors.length > 0 ? false : null"
											trim
										/>

										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-col>

								<!-- EMAIL -->
								<b-col
									md="4"
									cols="12"
									class="mb-2"
								>
									<label for="email">
										<strong>
											CORREO ELECTRÓNICO
											<span class="text-danger">*</span>
										</strong>
									</label>
									<validation-provider
										#default="{ errors }"
										name="Correo Electrónico"
										rules="required|email"
									>
										<b-form-input
											id="email"
											v-model="formData.email"
											placeholder="Ingrese correo electrónico..."
											:state="errors.length > 0 ? false : null"
											trim
										/>

										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-col>

								<!-- ROLES -->
								<b-col
									md="6"
									cols="12"
									class="mb-2"
								>
									<label for="role">
										<strong>
											ROL
											<span class="text-danger">*</span>
										</strong>
									</label>
									<validation-provider
										#default="{ errors }"
										rules="required"
										name="Role"
									>
										<v-select
											id="role"
											placeholder="Seleccione"
											v-model="formData.role"
											:state="errors.length > 0 ? false : null"
											:options="options.roles"
										/>

										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-col>

								<!-- DEPARTMENTS -->
								<b-col
									v-if="!isAdmin"
									md="6"
									cols="12"
									class="mb-2"
								>
									<label for="department">
										<strong>
											DEPARTAMENTO
											<span class="text-danger">*</span>
										</strong>
									</label>
									<validation-provider
										#default="{ errors }"
										rules="required"
										name="Departamento"
									>
										<v-select
											id="department"
											placeholder="Seleccione"
											v-model="formData.department"
											:state="errors.length > 0 ? false : null"
											:options="options.departments"
										/>

										<small class="text-danger">{{ errors[0] }}</small>
									</validation-provider>
								</b-col>
							</b-row>

							<template #footer>
								<!-- BUTTON SUBMIT -->
								<b-button-loading
									text="ACTUALIZAR"
									type="submit"
									:processing="processing"
								></b-button-loading>
							</template>
						</b-card>
					</b-col>
				</b-row>
			</b-form>
		</validation-observer>
	</component>
</template>

<script>
import useNotifications from "@notifications"
import BButtonLoading from "@core/components/b-button-loading/BButtonLoading.vue"
import { ref, computed, watch, onBeforeMount, onUnmounted } from "@vue/composition-api"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import userStoreModule from "../userStoreModule"
import { required, email } from "@validations"
import vSelect from "vue-select"
import router from "@/router"
import store from "@/store"
import axios from "@axios"

export default {
	components: {
		ValidationProvider,
		ValidationObserver,

		BButtonLoading,

		required,
		email,
		vSelect
	},
	setup() {
		// USE TOAST
		const { toastNotification } = useNotifications()

		// REFS
		const refForm = ref(null)
		const userData = ref(null)

		const formData = ref({
			names: null,
			surnames: null,
			email: null,
			department: null,
			role: null
		})

		const processing = ref(false)
		const options = ref({})

		// Check if selected role is admin
		const isAdmin = computed(() => {
			const roleLabel = formData.value.role?.label?.toLowerCase() || ""
			return roleLabel.includes("admin")
		})

		// Clear department when admin role is selected
		watch(isAdmin, (newValue) => {
			if (newValue) {
				formData.value.department = null
			}
		})

		const USER_APP_STORE_MODULE_NAME = "app-user"

		// REGISTER MODULE
		if (!store.hasModule(USER_APP_STORE_MODULE_NAME))
			store.registerModule(USER_APP_STORE_MODULE_NAME, userStoreModule)

		// UNREGISTER ON LEAVE
		onUnmounted(() => {
			if (store.hasModule(USER_APP_STORE_MODULE_NAME))
				store.unregisterModule(USER_APP_STORE_MODULE_NAME)
		})

		// CALL FUNCTION TO POPULATE OPTIONS FOR THE FILTERS
		onBeforeMount(async () => {
			await getSelectFilters()
			fetchUserData()
		})

		const getSelectFilters = async () => {
			const departments = await axios.get("/selectors/departments")
			const roles = await axios.get("/selectors/roles")
			options.value = {
				...departments.data,
				...roles.data
			}
		}

		const fetchUserData = () => {
			store
				.dispatch("app-user/fetchUser", {
					id: router.currentRoute.params.id
				})
				.then((response) => {
					if (response.data === "error") {
						userData.value = undefined
					} else {
						userData.value = response.data.user

						// Populate form with user data
						const role = options.value.roles.find(
							(item) => item.value === userData.value.role_id
						)
						const department = options.value.departments.find(
							(item) => item.value === userData.value.department_id
						)

						formData.value = {
							names: userData.value.names,
							surnames: userData.value.surnames,
							email: userData.value.email,
							department,
							role
						}
					}
				})
				.catch((error) => {
					if (error.response?.status === 404) {
						userData.value = undefined
					}
				})
		}

		const onSubmit = async () => {
			const isValid = await refForm.value.validate()

			if (!isValid) {
				toastNotification("Complete los campos requeridos.", false)
				return
			}

			processing.value = true

			const payload = {
				names: formData.value.names,
				surnames: formData.value.surnames,
				email: formData.value.email,
				role_id: formData.value.role?.value || null,
				department_id: formData.value.department?.value || null
			}

			store
				.dispatch("app-user/updateUser", {
					id: router.currentRoute.params.id,
					payload
				})
				.then((response) => {
					toastNotification(response.data.message)
					router.push({ name: "users-list" })
				})
				.catch((err) => {
					const message =
						err.response?.data?.message || "Error al actualizar usuario"
					toastNotification(message, false)
				})
				.finally(() => (processing.value = false))
		}

		return {
			// DATA
			processing,
			formData,
			options,
			userData,
			isAdmin,

			// REFS
			refForm,

			// METHODS
			onSubmit
		}
	}
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
